import type { Network } from '@mogul/api-client';
import { TransactionType } from '@mogul/api-client';
import {
  NATIVE_CURRENCY_ADDRESS,
  resolveSigningMessage,
  SignType,
} from '@mogul/shared';
import { signMessage } from '@wagmi/core';
import type { BigNumber, BigNumberish } from 'ethers';
import { ethers } from 'ethers';
import { useContext } from 'react';

import { transactionsApi } from '../api';
import { createTransaction } from '../api-helpers/marketplace-notifications';
import { MarketPlaceContext } from '../contexts/app-state-context/marketplace/marketplace.context';
import { Web3StateContext } from '../contexts/app-state-context/web3-state/context';
import type {
  MogulMarketplace,
  MogulMarketplaceERC721,
} from '../lib/contracts';
import {
  MogulMarketplaceERC721Factory,
  MogulMarketplaceFactory,
} from '../lib/contracts';
import { AssetType } from '../lib/graph/MogulMarketplace';
import {
  getEthListingTokenIndex,
  getEthListingTokenPrice,
  getStarsAddress,
  getStarsListingTokenIndex,
  getStarsListingTokenPrice,
  MARKETPLACE_ADDRESS,
  MARKETPLACE_ADDRESS_ERC_721,
  STARS_ADDRESS,
  weiToEth,
} from '../lib/smart-contract-utils';
import { useContract } from './contract.hooks';

const ERC1155_ARGS = [MogulMarketplaceFactory, MARKETPLACE_ADDRESS] as const;
const ERC721_ARGS = [
  MogulMarketplaceERC721Factory,
  MARKETPLACE_ADDRESS_ERC_721,
] as const;

interface MarketplaceProps {
  type: AssetType;
  overrideAddress?: string;
}

export const useMarketplaceContract = (props: MarketplaceProps) => {
  const { walletDetails } = useContext(Web3StateContext);
  const { notificationEmail, setNotificationEmail } =
    useContext(MarketPlaceContext);

  const { contract } = useContract<MogulMarketplace | MogulMarketplaceERC721>(
    /** @ts-expect-error - typescript issue not being able to determine type */
    ...(props.type === AssetType.Erc1155 ? ERC1155_ARGS : ERC721_ARGS),
    props.overrideAddress,
  );

  const auctions = async (auctionId: BigNumber) => {
    if (!contract) {
      return;
    }

    const auction = await contract.auctions(auctionId);
    const hasBid = auction.startingPrice.lt(auction.highestBid.amount);
    const bidAmount = weiToEth(
      hasBid ? auction.highestBid.amount : auction.startingPrice,
    );

    return { ...auction, bidAmount };
  };

  const processTransaction = (
    hash: string,
    t_type: TransactionType,
    network: string,
    listingId: number,
    signed?: string | undefined,
  ) =>
    new Promise(resolve => {
      let userEmail: string | null = null;

      if (notificationEmail) {
        userEmail = notificationEmail;
        setNotificationEmail('');
      }

      void createTransaction(
        userEmail,
        hash,
        t_type,
        network as Network,
        listingId,
        signed,
      ).then(() => resolve(userEmail));
    });

  return {
    address: contract ? contract.address : '',
    walletDetails,
    initialized: contract !== undefined,
    auctions,
    startInterval: (
      auctionId: BigNumber,
      walletAddress: string,
      setAuction: React.Dispatch<
        React.SetStateAction<{
          auctionId: BigNumber;
          bid: string;
          highestBidderAddress: string;
          isHighestBidder: boolean;
          winnerAddress: string;
          startDate: Date;
          endDate: Date;
        }>
      >,
    ) => {
      return setInterval(
        () => async () => {
          const auction = await auctions(auctionId);

          if (!auction) {
            return;
          }

          setAuction({
            auctionId,
            bid: auction.bidAmount,
            highestBidderAddress: auction.highestBid.bidder,
            isHighestBidder: auction.highestBid.bidder === walletAddress,
            winnerAddress: auction.highestBid.bidder,
            startDate: new Date(auction.startTime.mul(1000).toNumber()),
            endDate: new Date(auction.endTime.mul(1000).toNumber()),
          });
        },
        10000,
      );
    },
    isNotificationsEnabled: async () => {
      if (!walletDetails.address) {
        return { emailNotifs: false, email: '' };
      }

      try {
        const response = await transactionsApi.notifsEnabled({
          walletAddress: walletDetails.address,
        });

        return {
          emailNotifs: response.data.emailNotifs,
          email: response.data.email,
        };
      } catch (e) {
        return { emailNotifs: false, email: '' };
      }
    },
    setNotificationEmail,
    createAuction: async (
      tokenAddress: string,
      tokenId: BigNumberish,
      tokenAmount: BigNumberish,
      startingStarsPrice: BigNumberish,
      startTime: BigNumberish,
      endTime: BigNumberish,
      reservePrice: BigNumberish,
      isStarsAuction: boolean,
    ) => {
      if (!contract) {
        return;
      }

      const signed =
        notificationEmail && notificationEmail.length > 0
          ? await signMessage({
              message: resolveSigningMessage(SignType.email),
            })
          : undefined;

      const auctionToken = isStarsAuction
        ? STARS_ADDRESS[walletDetails.chain.type]
        : NATIVE_CURRENCY_ADDRESS;

      let tx;
      if (props.type === AssetType.Erc1155) {
        tx = await contract.createAuction(
          tokenAddress,
          tokenId,
          tokenAmount,
          startingStarsPrice,
          startTime,
          endTime,
          reservePrice,
          auctionToken,
          // {
          //   from: ''
          // }
          // undefined,
        );
      } else {
        tx = await (contract as MogulMarketplaceERC721).createAuction(
          tokenAddress,
          tokenId,
          startingStarsPrice,
          startTime,
          endTime,
          reservePrice,
          auctionToken,
          // {
          //   from: ''
          // }
          // undefined,
        );
      }

      await tx.wait();

      const txn = await contract.provider.getTransaction(tx.hash);
      const txn_receipt = await txn.wait();

      // TODO: possibly change receipt decoding
      const decode = ethers.utils.defaultAbiCoder.decode(
        // event AuctionCreated
        [
          'uint256', // auctionId
          'address', // seller
          'address', // tokenAddress
          'uint256', // tokenId
          ...(props.type === AssetType.Erc1155 ? ['uint256'] : []), // tokenAmount
          'uint256', // startingPrice
          'uint256', // startTime
          'uint256', // endTime
          'uint256', // reservePrice
          'address', // auctionToken
        ],
        txn_receipt.logs[props.type === AssetType.Erc1155 ? 1 : 2].data,
      );

      const auctionId = (decode[0] as ethers.BigNumber).toNumber();

      await processTransaction(
        tx.hash,
        TransactionType.CreateAuction,
        walletDetails.chain.type,
        auctionId,
        signed,
      );
    },
    createListing: async (
      tokenAddress: string,
      tokenId: BigNumberish,
      tokenAmount: BigNumberish,
      starsPrice: BigNumberish,
      ethPrice: BigNumberish,
      isStarsListing: boolean,
      isEthListing: boolean,
    ) => {
      if (!contract) {
        return;
      }

      const signed =
        notificationEmail && notificationEmail.length > 0
          ? await signMessage({
              message: resolveSigningMessage(SignType.email),
            })
          : undefined;

      const starsAddress = getStarsAddress();

      let tx;
      if (props.type === AssetType.Erc1155) {
        tx = await contract.createListing(
          tokenAddress,
          tokenId,
          tokenAmount,
          [
            ...(isStarsListing ? [starsPrice] : []),
            ...(isEthListing ? [ethPrice] : []),
          ],
          [
            ...(isStarsListing ? [starsAddress] : []),
            ...(isEthListing ? ([NATIVE_CURRENCY_ADDRESS] as string[]) : []),
          ],
        );
      } else {
        tx = await (contract as MogulMarketplaceERC721).createListing(
          tokenAddress,
          tokenId,
          [
            ...(isStarsListing ? [starsPrice] : []),
            ...(isEthListing ? [ethPrice] : []),
          ],
          [
            ...(isStarsListing ? [starsAddress] : []),
            ...(isEthListing ? ([NATIVE_CURRENCY_ADDRESS] as string[]) : []),
          ],
        );
      }
      await tx.wait();

      const txn = await contract.provider.getTransaction(tx.hash);
      const txn_receipt = await txn.wait();

      const decode = ethers.utils.defaultAbiCoder.decode(
        // event ListingCreated (https://testnet.bscscan.com/tx/0xdee8ded6c245a44731e6946bab538df7b30dd24b82d0a37ddfec44f5477d0da7#eventlog)
        [
          'uint256', // listingId
          'address', // seller
          'address', // tokenAddress
          'uint256', // tokenId
          // 'uint256',
          // 'uint256',
          ...(props.type === AssetType.Erc1155 ? ['uint256'] : []), // tokenAmount
          'uint256[]', // listingPrices
          // 'bool',
          // 'bool',
          'address[]', // listingTokens
        ],
        txn_receipt.logs[props.type === AssetType.Erc1155 ? 1 : 2].data,
      );

      const listingId = (decode[0] as ethers.BigNumber).toNumber();

      await processTransaction(
        tx.hash,
        TransactionType.CreateListing,
        walletDetails.chain.type,
        listingId,
        signed,
      );
    },

    cancelListing: async (listingId: string) => {
      if (!contract) {
        return;
      }

      const tx = await contract.cancelListing(listingId);
      await tx.wait();
    },

    cancelAuction: async (auctionId: string) => {
      if (!contract) {
        return;
      }

      const tx = await contract.cancelAuction(auctionId);
      await tx.wait();
    },

    claimAuction: async (auctionId: string) => {
      if (!contract) {
        return;
      }

      const tx = await contract.claimAuction(auctionId);
      await tx.wait();
    },

    placeBid: async (auctionId: BigNumberish, amount: BigNumberish) => {
      if (!contract) {
        return;
      }

      const signed =
        notificationEmail && notificationEmail.length > 0
          ? await signMessage({
              message: resolveSigningMessage(SignType.email),
            })
          : undefined;

      const auction = await contract.auctions(auctionId);

      let tx;
      // if (auction.isStarsAuction) {
      const starsAddress = getStarsAddress();
      if (auction.auctionToken === starsAddress) {
        tx = await contract.placeBid(auctionId, amount);
      } else {
        tx = await contract.placeBid(auctionId, amount, {
          value: amount,
        });
      }

      await tx.wait();

      await processTransaction(
        tx.hash,
        TransactionType.Bid,
        walletDetails.chain.type,
        auctionId as number,
        signed,
      );

      return tx;
    },

    buyTokens: async (
      listingId: BigNumberish,
      nftAmount: number,
      isStarsPurchase: boolean,
    ) => {
      if (!contract) {
        return;
      }

      const signed =
        notificationEmail && notificationEmail.length > 0
          ? await signMessage({
              message: resolveSigningMessage(SignType.email),
            })
          : undefined;

      const listing = await contract.listings(listingId);

      let tx;
      if (isStarsPurchase) {
        const starsAddress = getStarsAddress();
        const listingPriceStars = getStarsListingTokenPrice(
          listing.listingTokens,
          listing.listingPrices,
        );

        if (props.type === AssetType.Erc1155) {
          tx = await contract.buyTokens(
            listingId,
            nftAmount,
            listingPriceStars.mul(nftAmount).toString(),
            starsAddress,
          );
        } else {
          tx = await (contract as MogulMarketplaceERC721).buyTokens(
            listingId,
            // nftAmount,
            listingPriceStars.mul(nftAmount),
            // true,
            starsAddress,
          );
        }
      } else {
        const listingPriceEth = getEthListingTokenPrice(
          listing.listingTokens,
          listing.listingPrices,
        );
        if (props.type === AssetType.Erc1155) {
          tx = await contract.buyTokens(
            listingId,
            nftAmount,
            listingPriceEth.mul(nftAmount),
            // false,
            NATIVE_CURRENCY_ADDRESS,
            {
              value: listingPriceEth.mul(nftAmount),
            },
          );
        } else {
          tx = await (contract as MogulMarketplaceERC721).buyTokens(
            listingId,
            // nftAmount,
            listingPriceEth.mul(nftAmount),
            // false,
            NATIVE_CURRENCY_ADDRESS,
            {
              value: listingPriceEth.mul(nftAmount),
            },
          );
        }
      }

      await tx.wait();

      await processTransaction(
        tx.hash,
        TransactionType.Buy,
        walletDetails.chain.type,
        listingId as number,
        signed,
      );

      return tx;
    },

    changeListingPrice: async (
      listingId: string,
      newPrice: BigNumber,
      isStarsPrice: boolean,
    ) => {
      if (!contract) {
        return;
      }
      const listing = await contract.listings(listingId);
      const listingPrices = [...listing.listingPrices];
      if (isStarsPrice) {
        const starsListingTokenIndex = getStarsListingTokenIndex(
          listing.listingTokens,
        );
        listingPrices[starsListingTokenIndex] = newPrice;
      } else {
        const ethListingTokenIndex = getEthListingTokenIndex(
          listing.listingTokens,
        );
        listingPrices[ethListingTokenIndex] = newPrice;
      }

      const tx = await contract.changeListingPrice(
        listingId,
        listingPrices,
        // isStarsPrice,
      );
      await tx.wait();
    },

    getListingById: async (listingId: string) => {
      if (!contract) {
        return;
      }

      try {
        const listing = await contract.listings(listingId);

        return listing;
      } catch (error) {
        console.error(error);
      }
    },

    getAuctionById: async (auctionId: string) => {
      if (!contract) {
        return;
      }

      return contract.auctions(auctionId);
    },

    getCurrentBlockNumber: async () => {
      if (!contract) {
        return 0;
      }
      const blockNumber = await contract.provider.getBlockNumber();

      return blockNumber;
    },

    getNumListings: async () => {
      if (!contract) {
        return;
      }

      return contract.getNumListings();
    },

    getListingIds: async (listings: number[]) => {
      if (!contract) {
        return;
      }

      return contract.getListingIds(listings);
    },

    getNextListingId: async () => {
      if (!contract) {
        return;
      }

      return contract.nextListingId();
    },

    getListingsByIndexes: async (indexes: number[]) => {
      if (!contract) {
        return [];
      }

      const result: { [id: string]: any } = {};
      const listingId = await contract.getListingIds(indexes);
      const listingData = await contract.getListingsAtIndices(indexes);
      listingData.forEach(
        (listing: any, i: number) =>
          (result[listingId[i].toString()] = listing),
      );
      return result;
    },

    getNumAuctions: async () => {
      if (!contract) {
        return;
      }

      return contract.getNumAuctions();
    },

    getAuctionIds: async (indexes: number[]) => {
      if (!contract) {
        return;
      }

      return contract.getAuctionIds(indexes);
    },

    getNextAuctionId: async () => {
      if (!contract) {
        return;
      }

      return contract.nextAuctionId();
    },

    getAuctionsByIndexes: async (indexes: number[]) => {
      if (!contract) {
        return [];
      }

      const result: { [id: string]: any } = {};
      const auctionId = await contract.getAuctionIds(indexes);
      const auctionData = await contract.getAuctionsAtIndices(indexes);
      auctionData.forEach(
        (auction: any, i: number) =>
          (result[auctionId[i].toString()] = auction),
      );
      return result;
    },
  };
};
